import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
import _inheritsLoose from "@babel/runtime/helpers/inheritsLoose";
var _excluded = ["root", "backdrop"];
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MoreVertical from '@ellucian/ds-icons/lib/MoreVertical';
import ActionIconButton from './internal/ActionMenuIconButton';
import PopperWrapper from './internal/PopperWrapper';
import { withEDSContext } from './../EDSContext/EDSContext';
import { default as withStyles } from '../styles';
import { borderWidthThin } from '../styles/tokens';
import { Backdrop } from '@material-ui/core';
export var styles = function styles(theme) {
  return {
    root: {
      display: 'inline-block'
    },
    paper: {
      padding: 0,
      border: borderWidthThin + " solid " + theme.palette.grey[400]
    },
    backdrop: {
      // needs to be above content on the page, but below popper
      zIndex: theme.zIndex.actionMenu - 1
    }
  };
};
/**
 * Use `ActionMenu` to give users access to additional actions related to a field.
 * @done true
 * @updated v6.7.0
 * @versionAdded v0.0.8
 * @examples
 *  BasicActionMenuExamples
 *  ActionMenuWithCustomIcon
 *  ActionMenuItemWithIconAndLabel
 *  ActionMenuItemWithHeader
 *  ActionMenuExamplesWithArray
 *  ActionMenuExamplesWithDisabledItem
 *  PlacementActionMenuExamples
 *  ActionMenuWithScrollBehavior
 *  ActionMenuItemMobileVariant
 */

var ActionMenu = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(ActionMenu, _React$Component);

  function ActionMenu(props) {
    var _this;

    _this = _React$Component.call(this, props) || this;

    _this.handleToggle = function () {
      _this.setState({
        open: !_this.state.open
      });
    };

    _this.handleClose = function (event) {
      _this.setState({
        open: false
      });

      _this.focusActionMenuButton();
    };

    _this.focusActionMenuButton = function () {
      _this.buttonRef.current.focus();
    };

    _this.buttonRef = /*#__PURE__*/React.createRef();
    _this.placements = ['top-start', 'top-end'];
    _this.state = {
      // action menu is closed by default, unless an override is passed in
      // if action menu is opened, backdrop will follow suit -- one state controls both
      open: props.initialOpenOverride
    };
    return _this;
  }

  var _proto = ActionMenu.prototype;

  _proto.render = function render() {
    var _this$props = this.props,
        classes = _this$props.classes,
        placement = _this$props.placement,
        id = _this$props.id,
        edsContext = _this$props.edsContext,
        icon = _this$props.icon,
        className = _this$props.className,
        IconButtonProps = _this$props.IconButtonProps,
        PopperProps = _this$props.PopperProps,
        PaperProps = _this$props.PaperProps,
        children = _this$props.children;
    var open = this.state.open;

    var root = classes.root,
        backdrop = classes.backdrop,
        restClasses = _objectWithoutPropertiesLoose(classes, _excluded);

    var popperPlacement = placement ? placement : edsContext.dir === 'rtl' ? 'bottom-start' : 'bottom-end';
    var PopperPropsProps = Object.assign({}, PopperProps);
    var offset = 20; // Bring popper closer to icon
    // TODO: Do this with lodash or ES6 way.

    if (PopperPropsProps.modifiers) {
      PopperPropsProps.modifiers.offset = offset;
    } else {
      PopperPropsProps.modifiers = {
        offset: offset
      };
    }

    return /*#__PURE__*/React.createElement("div", {
      className: classNames(root, className)
    }, !open ? null : /*#__PURE__*/React.createElement(Backdrop, {
      invisible: true,
      open: open,
      onClick: this.handleClose,
      classes: {
        root: backdrop
      }
    }), /*#__PURE__*/React.createElement(ActionIconButton, {
      id: id,
      edsContext: edsContext,
      icon: icon,
      IconButtonProps: IconButtonProps,
      open: open,
      setOpen: this.handleToggle,
      buttonRef: this.buttonRef
    }), children && /*#__PURE__*/React.createElement(PopperWrapper, {
      classes: restClasses,
      placement: popperPlacement,
      id: id,
      edsContext: edsContext,
      PopperProps: PopperProps,
      PaperProps: PaperProps,
      open: open,
      handleClose: this.handleClose,
      buttonRef: this.buttonRef
    }, children));
  };

  return ActionMenu;
}(React.Component);

; // TODO: Make `children` prop required after `action` prop is removed

ActionMenu.propTypes = {
  /**
   * @ignore
   *
   * Classes used to extend original style object.
   */
  classes: PropTypes.object,

  /**
   * The content of the component.
   */
  children: PropTypes.node,

  /**
   * A CSS class that will be applied to the parent `div`.
   */
  className: PropTypes.string,

  /**
   * @ignore
   */
  edsContext: PropTypes.object,

  /**
   * Icon used in the icon button.
   *
   * Defaults to 'more-vertical' icon.
   */
  icon: PropTypes.node,

  /**
   * Menu HTML ID for uniqueness.
   */
  id: PropTypes.string,

  /**
   * **NOT APPLICABLE FOR MOBILE VARIANT**
   *
   * Placement is used to place the menu at start or end of the container or screen.
   *
   * Defaults to 'bottom-end' for LTR or 'bottom-start' for RTL.
   */
  placement: PropTypes.oneOf(['bottom-end', 'bottom-start']),

  /**
   * Props applied to the IconButton that brings up the action menu
  */
  IconButtonProps: PropTypes.object,

  /**
   * Props applied to the `Paper` component
  */
  PaperProps: PropTypes.object,

  /**
   * **NOT APPLICABLE FOR MOBILE VARIANT**
   *
   * Props applied to the `Popper` element.
   */
  PopperProps: PropTypes.object,

  /**
   * **APPLICABLE ONLY TO MOBILE VARIANT**
   *
   * Props applied to the `Drawer` element.
   */
  DrawerProps: PropTypes.object,

  /**
   * @ignore
   *
   * Externally set the initial open state of the action menu. For testing purposes only.
   */
  initialOpenOverride: PropTypes.bool,

  /**
   * **APPLICABLE ONLY TO MOBILE VARIANT**
   *
   * Title of the action menu for mobile variant
   */
  title: PropTypes.string
};
ActionMenu.defaultProps = {
  initialOpenOverride: false,
  icon: /*#__PURE__*/React.createElement(MoreVertical, null)
};
export default withEDSContext(withStyles(styles)(ActionMenu));